import { useState } from 'react';
import { Copy, ShieldOff, Trash2, Undo2 } from 'lucide-react';
import {
  PERMISSION_KEYS,
  ROLE_LABELS,
  ROLE_LEVEL,
  ROLE_PERMISSIONS,
  VIEW_ONLY_KEYS,
  expandPermissions,
  type PermissionKey,
} from '@supernova/shared';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useCreateTenantRole,
  useDeleteTenantRole,
  useTenantRoles,
  useUpdateTenantRole,
} from '@/hooks/use-tenant-roles';
import { toast } from '@/stores/toast-store';
import { BUILT_IN, BUILT_IN_SUMMARY, useUserAccess, type BuiltInRole } from './user-access';
import { PermissionGrid } from './permission-grid';

/** What a custom role is treated as on the register and in per-location settings. */
const REGISTER_ROLES = ['employee', 'inventory_manager', 'manager', 'admin'] as const;

const ROLES_HASH = '#/users/roles';

/** What each section's quick buttons in the permission grid do. */
const QUICK_SET_HELP = [
  { label: 'None', help: 'Clears every box in the section. The role gets no access to it.' },
  {
    label: 'View only',
    help: 'Ticks only the View boxes, so the role can open those screens but not create, edit or delete.',
  },
  { label: 'Full', help: 'Ticks every box in the section, including create, edit, delete and extra actions.' },
];

const goToRoles = () => {
  window.location.hash = ROLES_HASH;
};

interface Draft {
  id?: string;
  name: string;
  baseRole: string;
  permissions: PermissionKey[];
}

const isBuiltIn = (value: string): value is BuiltInRole => (BUILT_IN as readonly string[]).includes(value);

/** The draft a "new role" link starts from: `?from=view-only`, `?copy=<built-in>` or `?copy=custom:<id>`. */
function newDraftFromHash(roles: { id: string; name: string; baseRole: string; permissions: string[] }[]): Draft {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  if (params.get('from') === 'view-only') {
    return { name: 'View only', baseRole: 'employee', permissions: [...VIEW_ONLY_KEYS] };
  }
  const copy = params.get('copy') ?? '';
  if (isBuiltIn(copy)) {
    return {
      name: `${ROLE_LABELS[copy]} (copy)`,
      baseRole: copy === 'owner' ? 'admin' : copy,
      permissions: [...ROLE_PERMISSIONS[copy]],
    };
  }
  const source = copy.startsWith('custom:') ? roles.find((r) => `custom:${r.id}` === copy) : undefined;
  if (source) {
    return {
      name: `${source.name} (copy)`,
      baseRole: source.baseRole,
      permissions: expandPermissions(source.permissions),
    };
  }
  return { name: '', baseRole: 'employee', permissions: [] };
}

/**
 * One role on its own page. `param` is `new`, a built-in role (read-only, with
 * a copy button) or a custom role's id (editable). Owners and admins only.
 */
export function RoleEditorPage({ param }: { param: string }) {
  const { canManageRoles } = useUserAccess();
  const { data, isLoading } = useTenantRoles({ enabled: canManageRoles });
  const roles = data?.data ?? [];
  const createRole = useCreateTenantRole();
  const updateRole = useUpdateTenantRole();
  const deleteRole = useDeleteTenantRole();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ text: string; undo?: PermissionKey[] } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const builtIn = isBuiltIn(param) ? param : null;
  const isNew = param === 'new';
  const editingRole = !builtIn && !isNew ? roles.find((r) => r.id === param) : undefined;
  const saving = createRole.isPending || updateRole.isPending;

  // Start the draft once the roles it may copy from have loaded (set during
  // render, React's pattern for state that follows loaded data).
  if (!draft && !builtIn && !isLoading) {
    if (isNew) setDraft(newDraftFromHash(roles));
    else if (editingRole)
      setDraft({
        id: editingRole.id,
        name: editingRole.name,
        baseRole: editingRole.baseRole,
        permissions: expandPermissions(editingRole.permissions),
      });
  }

  const edit = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });

  const fillFrom = (label: string, keys: readonly string[]) => {
    if (!draft) return;
    const previous = draft.permissions;
    edit({ permissions: expandPermissions(keys) });
    setNotice({
      text: keys.length ? `Filled from ${label}. Adjust the boxes below.` : 'All boxes cleared.',
      undo: previous,
    });
  };

  const change = (add: PermissionKey[], remove: PermissionKey[]) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    add.forEach((k) => next.add(k));
    remove.forEach((k) => next.delete(k));
    edit({ permissions: PERMISSION_KEYS.filter((k) => next.has(k)) });
    setNotice(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setError('');
    if (!draft.name.trim()) {
      setError('Role name is required');
      return;
    }
    const input = { name: draft.name.trim(), baseRole: draft.baseRole, permissions: draft.permissions };
    try {
      if (draft.id) await updateRole.mutateAsync({ id: draft.id, ...input });
      else await createRole.mutateAsync(input);
      toast.success(draft.id ? 'Role saved' : 'Role created');
      goToRoles();
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to save role');
    }
  };

  const confirmDelete = async () => {
    if (!editingRole) return;
    setError('');
    try {
      await deleteRole.mutateAsync(editingRole.id);
      setDeleting(false);
      toast.success('Role deleted');
      goToRoles();
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to delete role');
      setDeleting(false);
    }
  };

  const title = builtIn ? ROLE_LABELS[builtIn] : isNew ? 'New Role' : (editingRole?.name ?? 'Role');
  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
      <a href="#/users" className="hover:text-foreground hover:underline">
        User Management
      </a>
      <span aria-hidden="true"> / </span>
      <a href={ROLES_HASH} className="hover:text-foreground hover:underline">
        Roles and Permissions
      </a>
      <span aria-hidden="true"> / </span>
      <span className="text-foreground">{title}</span>
    </nav>
  );
  const blocked = (heading: string, description: string) => (
    <div className="space-y-4">
      {breadcrumb}
      <PageHeader title={title} backTo={ROLES_HASH} backLabel="Back to roles" />
      <div className="rounded-xl border border-border bg-white">
        <EmptyState
          icon={ShieldOff}
          title={heading}
          description={description}
          action={
            <a
              href={ROLES_HASH}
              className="inline-flex h-9 items-center rounded-lg border border-border bg-white px-4 text-sm font-medium hover:bg-muted"
            >
              Back to roles
            </a>
          }
        />
      </div>
    </div>
  );

  if (!canManageRoles) return blocked("You can't manage roles", 'Only owners and admins can change roles.');
  if (!builtIn && !draft) {
    if (isLoading) return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
    if (!isNew && !editingRole) return blocked('Role not found', 'It may have been deleted.');
  }

  // ── A built-in role: read-only, with a copy button ──
  if (builtIn) {
    return (
      <div className="space-y-5">
        {breadcrumb}
        <PageHeader
          title={title}
          description={`Built-in role · ${BUILT_IN_SUMMARY[builtIn]}. It can't be changed, but you can copy it to start a custom role.`}
          backTo={ROLES_HASH}
          backLabel="Back to roles"
        >
          <a
            href={`${ROLES_HASH}/new?copy=${builtIn}`}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-primary px-4 text-sm font-semibold text-primary hover:bg-primary/5"
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            Copy to a new role
          </a>
        </PageHeader>
        <section className="rounded-xl border border-border bg-white p-5 sm:p-6">
          <PermissionGrid
            key={builtIn}
            isChecked={(key) => ROLE_PERMISSIONS[builtIn].includes(key)}
            isLocked={() => true}
            onChange={() => {}}
            roleLevel={ROLE_LEVEL[builtIn]}
          />
        </section>
      </div>
    );
  }

  if (!draft) return null;

  const sources = [
    { label: 'View only (every tab)', keys: VIEW_ONLY_KEYS },
    ...BUILT_IN.filter((r) => r !== 'owner').map((r) => ({ label: ROLE_LABELS[r], keys: ROLE_PERMISSIONS[r] })),
    ...roles.filter((r) => r.id !== draft.id).map((r) => ({ label: r.name, keys: r.permissions })),
  ];

  // ── A new or custom role: editable ──
  return (
    <div className="space-y-5">
      {breadcrumb}
      <PageHeader
        title={title}
        description={
          editingRole
            ? `Custom role · Used by ${editingRole.userCount} ${editingRole.userCount === 1 ? 'user' : 'users'}.`
            : 'Choose what this role can see and do. The sections follow the sidebar, so a box here is a screen or button there.'
        }
        backTo={ROLES_HASH}
        backLabel="Back to roles"
      >
        {editingRole && (
          <button
            type="button"
            onClick={() => setDeleting(true)}
            disabled={editingRole.userCount > 0}
            title={editingRole.userCount > 0 ? 'Move its users to another role first' : undefined}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete role
          </button>
        )}
      </PageHeader>

      {error && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={save} noValidate className="space-y-5">
        <section className="rounded-xl border border-border bg-white p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="role-name" className="mb-1 block text-sm font-medium">
                Role name
              </label>
              <input
                id="role-name"
                type="text"
                value={draft.name}
                maxLength={100}
                onChange={(e) => edit({ name: e.target.value })}
                placeholder="e.g. Senior Cashier"
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="role-register" className="mb-1 block text-sm font-medium">
                On the register, treat as
              </label>
              <select
                id="role-register"
                value={draft.baseRole}
                onChange={(e) => edit({ baseRole: e.target.value })}
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                {REGISTER_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Register permissions, report visibility and cost visibility are set per location for each built-in role.
                This role follows the {ROLE_LABELS[draft.baseRole]} settings there.
                {draft.baseRole === 'admin' && ' Admin also opens the Owner and Admin only screens below.'}
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <span className="block text-sm font-medium">Fill the boxes from</span>
            <div className="flex flex-wrap gap-2">
              {sources.map((source) => (
                <button
                  key={source.label}
                  type="button"
                  onClick={() => fillFrom(source.label, source.keys)}
                  className="min-h-9 rounded-full border border-input bg-white px-3.5 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {source.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => fillFrom('Nothing', [])}
                className="min-h-9 rounded-full border border-input bg-white px-3.5 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Nothing
              </button>
            </div>
            {notice && (
              <div
                role="status"
                className="flex flex-wrap items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800"
              >
                {notice.text}
                {notice.undo && (
                  <button
                    type="button"
                    onClick={() => {
                      edit({ permissions: notice.undo! });
                      setNotice(null);
                    }}
                    className="inline-flex items-center gap-1 font-semibold text-primary"
                  >
                    <Undo2 className="h-3.5 w-3.5" aria-hidden="true" /> Undo
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        <section
          aria-labelledby="role-permissions-heading"
          className="relative rounded-xl border border-border bg-white p-5 sm:p-6"
        >
          <div className="mb-4">
            <h2 id="role-permissions-heading" className="text-base font-semibold">
              Permissions
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Tick boxes one by one, or use the quick buttons on each section's row to set the whole section at once.
            </p>
            <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {QUICK_SET_HELP.map(({ label, help }) => (
                <div key={label} className="flex items-start gap-2.5 rounded-lg bg-muted/50 p-3">
                  <dt className="shrink-0">
                    <span className="inline-flex h-7 items-center rounded-md border border-border bg-white px-2 text-xs text-muted-foreground">
                      {label}
                    </span>
                  </dt>
                  <dd className="text-xs text-muted-foreground">{help}</dd>
                </div>
              ))}
            </dl>
          </div>
          <PermissionGrid
            key={draft.id ?? 'new'}
            isChecked={(key) => draft.permissions.includes(key)}
            isLocked={() => false}
            onChange={change}
            roleLevel={ROLE_LEVEL[draft.baseRole] ?? 0}
          />
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-4 sm:px-6">
          <span className="text-sm text-muted-foreground">
            {draft.permissions.length} permissions ticked.
            {draft.id && ' Changes reach this role’s users within 15 minutes.'}
          </span>
          <div className="flex flex-wrap gap-2">
            <a
              href={ROLES_HASH}
              className="inline-flex h-10 items-center rounded-lg border border-border bg-white px-5 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex h-10 items-center rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save role'}
            </button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={deleting}
        title="Delete role"
        message={`Delete ${editingRole?.name}? This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(false)}
      />
    </div>
  );
}
